import React from 'react';

import { observer } from 'mobx-react';

import { useNavigate, useParams } from 'react-router-dom';

import { isAxiosError } from 'axios';

import Record from 'ui/Record/Record';

import MainHeader from 'components/MainHeader';

import { CenteredCircularProgress } from 'components/Progress';

import API from 'utils/api';

import { useStore } from 'utils/hooks/store';

import { useNavBarHeight } from 'utils/hooks/navbar';

import { isNotFound } from 'utils/api/errors';

import routes from 'core/routes';

import { SLASH_FULL_DATE_FORMAT, UserRole } from 'core/constants';

import { exportTimeZoneParams, formatFloating, formatMomentDate } from 'utils/formatters/timezone';

import { AcknowledgementRequestDetail, AcknowledgementRequestStatus } from 'utils/api/modules/entities/entities.types';

import RequestStatsTiles from './components/RequestStatsTiles/RequestStatsTiles';
import RequestRecipientsGrid from './components/RequestRecipientsGrid';
import CancelRequestModal from './components/CancelRequestModal';
import RequestKebabMenu from './components/RequestKebabMenu';
import { Subtitle } from './styles';

const AcknowledgementRequestDetails: React.FunctionComponent = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();
  const navigate = useNavigate();
  const { requestId } = useParams<{ requestId: string }>();
  const { notification, session } = useStore();

  const [request, setRequest] = React.useState<AcknowledgementRequestDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);

  const refreshRef = React.useRef<(() => void) | null>(null);

  React.useEffect(() => {
    if (!requestId) return undefined;
    const apiRequest = API.entities.getAcknowledgementRequest(requestId);
    let cancelled = false;
    setLoading(true);
    apiRequest()
      .then(({ data }) => {
        if (!cancelled) {
          setRequest(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (isAxiosError(err) && isNotFound(err)) {
          notification.enqueueInfoSnackbar('This request could not be found');
          navigate(routes.dashboard.documents.acknowledgementRequests.toString());
          return;
        }
        notification.enqueueErrorSnackbar('Could not load acknowledgement request.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [requestId, navigate, notification]);

  const onRefreshCallback = React.useCallback((callback: () => void) => {
    refreshRef.current = callback;
  }, []);

  const refetchHeader = React.useCallback(async (): Promise<void> => {
    if (!requestId) return;
    try {
      const { data } = await API.entities.getAcknowledgementRequest(requestId)();
      setRequest(data);
    } catch (err) {
      // Header refresh failure shouldn't take down the page; refresh next time the user revisits.
    }
  }, [requestId]);

  const handleCancel = async (): Promise<void> => {
    if (!requestId) return;
    setCancelling(true);
    try {
      const { data } = await API.entities.cancelAcknowledgementRequest(requestId)();
      setRequest(data);
      refreshRef.current?.();
      notification.enqueueSuccessSnackbar('Request cancelled');
      setCancelOpen(false);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        const detail = (
          err.response.data && typeof err.response.data === 'object' && 'detail' in err.response.data
            ? String((err.response.data as { detail: string }).detail)
            : 'This request can no longer be cancelled.'
        ) as string;
        notification.enqueueErrorSnackbar(detail);
        await refetchHeader();
        refreshRef.current?.();
        setCancelOpen(false);
      } else {
        notification.enqueueErrorSnackbar('Could not cancel the request. Please try again.');
      }
    } finally {
      setCancelling(false);
    }
  };

  const handleExport = (): void => {
    if (!requestId) return;
    window.location.href = API.entities.acknowledgementRequestCSVUrl(requestId, exportTimeZoneParams());
  };

  if (loading || !request) {
    return <CenteredCircularProgress />;
  }

  const currentUser = session.user;
  const canCancel =
    request.status === AcknowledgementRequestStatus.Active &&
    !!currentUser &&
    (currentUser.uuid === request.owner?.uuid || currentUser.role === UserRole.Admin);

  return (
    <Record
      navBarHeight={navBarHeight}
      Header={
        <MainHeader
          title={request.name}
          breadcrumbs={[
            { label: 'Documents', link: routes.dashboard.documents.toString() },
            {
              label: 'Acknowledgements',
              link: routes.dashboard.documents.acknowledgementRequests.toString(),
            },
            {
              label: request.name,
              link: '',
            },
          ]}
          customHeaderTitle={
            <div>
              <h1 id={`record-title-${request.name}`} style={{ margin: 0 }}>
                {request.name}
              </h1>
              <Subtitle>
                <span>{request.document?.name ?? '—'}</span>
                <span>
                  · Sent{' '}
                  <span data-testid="acknowledgement-request-sent">
                    {formatMomentDate(request.createdAt, SLASH_FULL_DATE_FORMAT)}
                  </span>
                </span>
                <span>
                  · Due{' '}
                  <span data-testid="acknowledgement-request-due">
                    {formatFloating(request.dueDate, 'date', SLASH_FULL_DATE_FORMAT)}
                  </span>
                </span>
              </Subtitle>
            </div>
          }
          action={
            <RequestKebabMenu canCancel={canCancel} onCancel={() => setCancelOpen(true)} onExport={handleExport} />
          }
        />
      }
      Content={() => (
        <>
          <RequestStatsTiles request={request} />
          <RequestRecipientsGrid requestUuid={request.uuid} onRefreshCallback={onRefreshCallback} />
          <CancelRequestModal
            open={cancelOpen}
            cancelling={cancelling}
            request={request}
            onClose={() => setCancelOpen(false)}
            onConfirm={handleCancel}
          />
        </>
      )}
    />
  );
};

export default observer(AcknowledgementRequestDetails);
